import type { TablePreferences } from '@/application/ports/TablePreferences';

const PREFIX = 'ceramica.sort.';
const FOLD_PREFIX = 'ceramica.fold.';
const SETTING_PREFIX = 'ceramica.setting.';
const SIZE_PREFIX = 'ceramica.pageSize.';

/**
 * The remembered table order, in localStorage.
 *
 * Every access is guarded, as `LocalInstallPreferences` is and for the same
 * reason: private browsing and some managed-device policies make
 * `localStorage` throw on read as well as on write, and not remembering which
 * order somebody likes their table in is never a reason to fail to draw the
 * table. When storage is unavailable every table opens in its default order,
 * which is the right way for a convenience to fail.
 */
export class LocalTablePreferences implements TablePreferences {
  order(tableKey: string): string | null {
    try {
      return localStorage.getItem(PREFIX + tableKey);
    } catch {
      return null;
    }
  }

  rememberOrder(tableKey: string, presetKey: string): void {
    try {
      localStorage.setItem(PREFIX + tableKey, presetKey);
    } catch {
      /* see the note on the class */
    }
  }

  pageSize(tableKey: string): number | null {
    try {
      const stored = Number(localStorage.getItem(SIZE_PREFIX + tableKey));
      // A stored value that is not a positive whole number is somebody else's
      // key or a half written one, and the default is the right answer to both.
      return Number.isInteger(stored) && stored > 0 ? stored : null;
    } catch {
      return null;
    }
  }

  rememberPageSize(tableKey: string, size: number): void {
    try {
      localStorage.setItem(SIZE_PREFIX + tableKey, String(size));
    } catch {
      /* see the note on the class */
    }
  }

  folded(panelKey: string): boolean | null {
    try {
      const stored = localStorage.getItem(FOLD_PREFIX + panelKey);
      return stored === null ? null : stored === 'true';
    } catch {
      return null;
    }
  }

  rememberFolded(panelKey: string, folded: boolean): void {
    try {
      localStorage.setItem(FOLD_PREFIX + panelKey, String(folded));
    } catch {
      /* see the note on the class */
    }
  }

  setting(key: string): boolean | null {
    try {
      const stored = localStorage.getItem(SETTING_PREFIX + key);
      return stored === null ? null : stored === 'true';
    } catch {
      return null;
    }
  }

  rememberSetting(key: string, on: boolean): void {
    try {
      localStorage.setItem(SETTING_PREFIX + key, String(on));
    } catch {
      /* see the note on the class */
    }
  }
}
